/**
 * EditBillingDetailsDialog
 *
 * Reusable dialog for editing the rich billing details on a Client record.
 * Used from:
 *   - /clients (Clients list page) — full-fat create/edit
 *   - /leads/:id (Lead detail page) — opens with seeded values from the lead;
 *     creates a new Client and stamps `sourced_from_lead_id` for re-lookup
 *
 * Data principle (Iter 24): edits made here update ONLY the Client master.
 * Historical Proposals/Invoices/Credit Notes never mutate — they have a
 * frozen `bill_to` snapshot taken at creation time.
 *
 * On-save behaviour:
 *   - If `client` prop passed → PATCH /billing/client/{id}     (edit)
 *   - Else                  → POST /billing/client/create     (create)
 *   - Calls onSaved(updatedClient) so the host page can refresh.
 */
import { useEffect, useState, useMemo, useRef } from "react";
import { Buildings, IdentificationBadge, MapPinLine, EnvelopeSimple, Receipt } from "@phosphor-icons/react";
import { api, formatApiError } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { DialogFooter } from "./ui/dialog";
import FormDialog, { FormDialogCancel } from "./FormDialog";
import { toast } from "sonner";

// 36 Indian states + UTs — anchored to the supplier's state for GST split logic.
const INDIAN_STATES = [
    { code: "AP", name: "Andhra Pradesh" }, { code: "AR", name: "Arunachal Pradesh" },
    { code: "AS", name: "Assam" }, { code: "BR", name: "Bihar" }, { code: "CG", name: "Chhattisgarh" },
    { code: "GA", name: "Goa" }, { code: "GJ", name: "Gujarat" }, { code: "HR", name: "Haryana" },
    { code: "HP", name: "Himachal Pradesh" }, { code: "JH", name: "Jharkhand" }, { code: "KA", name: "Karnataka" },
    { code: "KL", name: "Kerala" }, { code: "MP", name: "Madhya Pradesh" }, { code: "MH", name: "Maharashtra" },
    { code: "MN", name: "Manipur" }, { code: "ML", name: "Meghalaya" }, { code: "MZ", name: "Mizoram" },
    { code: "NL", name: "Nagaland" }, { code: "OD", name: "Odisha" }, { code: "PB", name: "Punjab" },
    { code: "RJ", name: "Rajasthan" }, { code: "SK", name: "Sikkim" }, { code: "TN", name: "Tamil Nadu" },
    { code: "TS", name: "Telangana" }, { code: "TR", name: "Tripura" }, { code: "UP", name: "Uttar Pradesh" },
    { code: "UK", name: "Uttarakhand" }, { code: "WB", name: "West Bengal" },
    { code: "AN", name: "Andaman & Nicobar" }, { code: "CH", name: "Chandigarh" }, { code: "DH", name: "Dadra & Nagar Haveli and Daman & Diu" },
    { code: "DL", name: "Delhi" }, { code: "JK", name: "Jammu & Kashmir" }, { code: "LA", name: "Ladakh" },
    { code: "LD", name: "Lakshadweep" }, { code: "PY", name: "Puducherry" },
];

const GST_TYPES = ["Intra-state", "Inter-state", "Export"];

function blank() {
    return {
        client_code: "",
        name: "", legal_name: "",
        gstin: "", pan: "", cin: "",
        state_code: "", state_name: "", country: "India",
        place_of_supply: "", gst_type: "",
        // Iter 43 — international billing support. Default to domestic
        // (preserves existing behaviour for the 99% case).
        is_international: false,
        gst_applicable: true,
        address: "", address_line1: "", address_line2: "",
        city: "", pin_code: "",
        contact_name: "", contact_email: "", contact_phone: "",
        billing_contact_name: "", billing_email: "", billing_phone: "",
        segment: "",
        sourced_from_lead_id: null,
    };
}

export default function EditBillingDetailsDialog({
    open,
    onOpenChange,
    client,         // existing client to edit; null = create-mode
    seed,           // pre-fill values when opening from a Lead
    leadId,         // when present, sets sourced_from_lead_id on a newly created client
    onSaved,        // (updatedClient) => void — host can refresh
    headline,       // optional override for dialog title
}) {
    const [form, setForm] = useState(blank());
    const [busy, setBusy] = useState(false);
    const isEdit = Boolean(client?.id);
    // Snapshot of the form at last open — used to detect unsaved changes.
    const baselineRef = useRef(blank());

    // Hydrate the form whenever the dialog opens or the client/seed changes
    useEffect(() => {
        if (!open) return;
        const next = client
            ? { ...blank(), ...client }
            : { ...blank(), ...(seed || {}), sourced_from_lead_id: leadId ?? null };
        setForm(next);
        baselineRef.current = next;
    }, [open, client, seed, leadId]);

    // Compute dirty state by comparing current form against the baseline.
    // Cheap shallow JSON compare is enough — fields are flat strings/numbers.
    const isDirty = useMemo(
        () => JSON.stringify(form) !== JSON.stringify(baselineRef.current),
        [form],
    );

    const set = (patch) => setForm((f) => ({ ...f, ...patch }));

    // Auto-derive state_name when state_code changes
    const onStateCodeChange = (code) => {
        const match = INDIAN_STATES.find((s) => s.code === code);
        set({
            state_code: code,
            state_name: match?.name || form.state_name,
            place_of_supply: form.place_of_supply || match?.name || "",
        });
    };

    // Auto-pre-fill PAN from GSTIN positions 3..12 (validated server-side too)
    const onGstinChange = (g) => {
        const upper = (g || "").toUpperCase();
        const derivedPan = upper.length === 15 ? upper.slice(2, 12) : form.pan;
        set({ gstin: upper, pan: form.pan || derivedPan });
    };

    const submit = async (e) => {
        e?.preventDefault?.();
        // Validate the hard-required fields up-front so the user gets a clear toast
        if (!form.name?.trim()) {
            toast.error("Display name is required");
            return;
        }
        setBusy(true);
        try {
            // Drop empty optional links so Pydantic accepts a clean payload
            const payload = { ...form };
            if (!payload.sourced_from_lead_id) delete payload.sourced_from_lead_id;
            let saved;
            if (isEdit) {
                const { data } = await api.patch(`/billing/client/${client.id}`, payload);
                saved = data;
                toast.success("Billing details updated");
            } else {
                const { data } = await api.post("/billing/client/create", payload);
                saved = data;
                toast.success("Client created with billing details");
            }
            // Reset baseline → form is no longer "dirty" → close passes through
            // the FormDialog guard cleanly without showing the discard prompt.
            baselineRef.current = form;
            onSaved?.(saved);
            onOpenChange(false);
        } catch (err) {
            toast.error(formatApiError(err, "Failed to save billing details"));
        } finally {
            setBusy(false);
        }
    };

    return (
        <FormDialog
            open={open}
            onOpenChange={(v) => !busy && onOpenChange(v)}
            isDirty={isDirty}
            contentClassName="max-w-3xl max-h-[90vh] overflow-y-auto"
            testId="edit-billing-details-dialog"
            header={
                <div className="space-y-1.5">
                    <div className="flex items-center gap-2 text-lg font-semibold">
                        <Buildings size={18} weight="duotone" className="text-indigo-600" />
                        {headline || (isEdit ? `Edit billing details · ${client.name}` : "Capture billing details")}
                    </div>
                    <div className="text-xs text-slate-500">
                        These details are <strong>frozen onto every Proposal, Invoice and Credit Note at creation time</strong>.
                        Editing them here updates the Client master only — historical documents are never mutated.
                    </div>
                </div>
            }
        >
            <form onSubmit={submit} className="space-y-6 py-2">
                    {/* ── Iter 43 — Client Type & Tax Profile ─────────────── */}
                    <div
                        className="rounded-md border border-slate-200 bg-slate-50/60 px-4 py-3.5"
                        data-testid="bill-section-client-type"
                    >
                        <div className="flex items-center justify-between gap-4 flex-wrap">
                            <div>
                                <div className="reedos-eyebrow text-[10px] text-slate-500">Client type</div>
                                <p className="text-[11px] text-slate-500 mt-0.5 leading-snug max-w-md">
                                    International clients relax GSTIN / PAN / state / PIN requirements and disable GST on invoices.
                                </p>
                            </div>
                            <div className="inline-flex rounded-md border border-slate-200 bg-white p-0.5 shrink-0">
                                <button
                                    type="button"
                                    onClick={() => set({
                                        is_international: false,
                                        gst_applicable: true,
                                        country: form.country && form.country.toLowerCase() !== "india" ? "India" : (form.country || "India"),
                                    })}
                                    className={`px-3 py-1.5 text-xs font-medium rounded ${!form.is_international ? "bg-slate-900 text-white" : "text-slate-600 hover:text-slate-900"}`}
                                    data-testid="bill-toggle-domestic"
                                >
                                    Domestic (India)
                                </button>
                                <button
                                    type="button"
                                    onClick={() => set({
                                        is_international: true,
                                        gst_applicable: false,
                                        country: form.country && form.country.toLowerCase() !== "india" ? form.country : "",
                                    })}
                                    className={`px-3 py-1.5 text-xs font-medium rounded ${form.is_international ? "bg-amber-600 text-white" : "text-slate-600 hover:text-slate-900"}`}
                                    data-testid="bill-toggle-international"
                                >
                                    International
                                </button>
                            </div>
                        </div>
                        {/* Independent GST applicability override — rare but supported
                            (e.g. SEZ domestic, captive subsidiary reverse charge). */}
                        <label className="mt-3 flex items-center gap-2 cursor-pointer select-none">
                            <input
                                type="checkbox"
                                checked={!!form.gst_applicable}
                                onChange={(e) => set({ gst_applicable: e.target.checked })}
                                className="h-3.5 w-3.5 rounded border-slate-300"
                                data-testid="bill-gst-applicable-toggle"
                            />
                            <span className="text-xs text-slate-700">
                                GST applicable on invoices
                                <span className="text-[10px] text-slate-500 ml-1">
                                    {form.is_international
                                        ? "(usually off for international clients)"
                                        : "(usually on for domestic clients)"}
                                </span>
                            </span>
                        </label>
                    </div>

                    {/* ── Identity ─────────────────────────────────────── */}
                    <Section icon={<IdentificationBadge size={14} weight="duotone" />} title="Company Identity">
                        {/* Iter 49.0 — Operator-supplied natural key. Editable on
                            create (auto-defaulted from system id when blank).
                            READ-ONLY on edit: natural keys are immutable in
                            Iter 49.0 to preserve referential coupling for the
                            forthcoming CSV import framework. */}
                        <Field
                            label="Client code"
                            hint={isEdit
                                ? "Natural key — set on create, immutable thereafter."
                                : "Operator-supplied unique slug. Leave blank to auto-default to the system id."}
                        >
                            <Input
                                value={form.client_code || ""}
                                onChange={(e) => set({ client_code: e.target.value })}
                                placeholder={isEdit ? "" : "e.g. ACME-IND (optional)"}
                                className="font-mono text-xs"
                                readOnly={isEdit}
                                disabled={isEdit}
                                maxLength={64}
                                data-testid="bill-field-client-code"
                            />
                        </Field>
                        <Field label="Display Name *" required>
                            <Input
                                value={form.name}
                                onChange={(e) => set({ name: e.target.value })}
                                placeholder="Short name used in lists / pickers"
                                data-testid="bill-field-name"
                            />
                        </Field>
                        <Field label="Legal Name">
                            <Input
                                value={form.legal_name}
                                onChange={(e) => set({ legal_name: e.target.value })}
                                placeholder="Full legal name as printed on Tax Invoice"
                                data-testid="bill-field-legal-name"
                            />
                        </Field>
                        <Field label={form.is_international ? "GSTIN (optional)" : "GSTIN"} hint={form.is_international ? "Optional for international clients" : "15 chars · auto-derives PAN, state code on the fly"}>
                            <Input
                                value={form.gstin}
                                onChange={(e) => onGstinChange(e.target.value)}
                                placeholder={form.is_international ? "Skip for foreign clients" : "27AABCU9603R1ZX"}
                                className="font-mono uppercase tracking-wider"
                                maxLength={15}
                                data-testid="bill-field-gstin"
                            />
                        </Field>
                        <Field label={form.is_international ? "PAN (optional)" : "PAN"}>
                            <Input
                                value={form.pan}
                                onChange={(e) => set({ pan: (e.target.value || "").toUpperCase() })}
                                placeholder={form.is_international ? "Skip for foreign clients" : "AAACB1234A"}
                                className="font-mono uppercase"
                                maxLength={10}
                                data-testid="bill-field-pan"
                            />
                        </Field>
                        <Field label="CIN" hint="Optional · MCA-issued company ID">
                            <Input
                                value={form.cin}
                                onChange={(e) => set({ cin: (e.target.value || "").toUpperCase() })}
                                placeholder="U74999MH2018PTC312345"
                                className="font-mono uppercase"
                                maxLength={21}
                                data-testid="bill-field-cin"
                            />
                        </Field>
                    </Section>

                    {/* ── Billing Address ─────────────────────────────── */}
                    <Section icon={<MapPinLine size={14} weight="duotone" />} title="Billing Address">
                        <Field label="Address Line 1" wide>
                            <Input
                                value={form.address_line1}
                                onChange={(e) => set({ address_line1: e.target.value })}
                                placeholder="Building / Office number, Street"
                                data-testid="bill-field-addr1"
                            />
                        </Field>
                        <Field label="Address Line 2" wide>
                            <Input
                                value={form.address_line2}
                                onChange={(e) => set({ address_line2: e.target.value })}
                                placeholder="Area / Locality (optional)"
                                data-testid="bill-field-addr2"
                            />
                        </Field>
                        <Field label="City">
                            <Input
                                value={form.city}
                                onChange={(e) => set({ city: e.target.value })}
                                placeholder="Mumbai"
                                data-testid="bill-field-city"
                            />
                        </Field>
                        <Field label={form.is_international ? "State / Province (free-form)" : "State"}>
                            {form.is_international ? (
                                <Input
                                    value={form.state_name}
                                    onChange={(e) => set({ state_name: e.target.value, state_code: "" })}
                                    placeholder="e.g. Dubai · Singapore · California"
                                    data-testid="bill-field-state-international"
                                />
                            ) : (
                                <select
                                    value={form.state_code}
                                    onChange={(e) => onStateCodeChange(e.target.value)}
                                    className="w-full h-9 px-3 rounded border border-slate-200 bg-white text-sm focus:outline-none focus:border-indigo-500"
                                    data-testid="bill-field-state"
                                >
                                    <option value="">— Select —</option>
                                    {INDIAN_STATES.map((s) => (
                                        <option key={s.code} value={s.code}>{s.code} · {s.name}</option>
                                    ))}
                                </select>
                            )}
                        </Field>
                        <Field label={form.is_international ? "Postal / ZIP code (optional)" : "PIN Code"}>
                            <Input
                                value={form.pin_code}
                                onChange={(e) => set({ pin_code: e.target.value })}
                                placeholder={form.is_international ? "e.g. 110002 · SE1 8XX" : "400001"}
                                className="font-mono"
                                maxLength={form.is_international ? 16 : 6}
                                data-testid="bill-field-pin"
                            />
                        </Field>
                        <Field label="Country" hint={form.is_international ? "Important for international clients (UAE, Singapore, US, UK …)" : ""}>
                            <Input
                                value={form.country}
                                onChange={(e) => set({ country: e.target.value })}
                                placeholder={form.is_international ? "e.g. UAE · Singapore · United States" : "India"}
                                data-testid="bill-field-country"
                            />
                        </Field>
                    </Section>

                    {/* ── Billing Contact ─────────────────────────────── */}
                    <Section icon={<EnvelopeSimple size={14} weight="duotone" />} title="Billing Contact">
                        <Field label="Billing Contact Name">
                            <Input
                                value={form.billing_contact_name}
                                onChange={(e) => set({ billing_contact_name: e.target.value })}
                                placeholder="Accounts Payable owner"
                                data-testid="bill-field-bcname"
                            />
                        </Field>
                        <Field label="Billing Email">
                            <Input
                                type="email"
                                value={form.billing_email}
                                onChange={(e) => set({ billing_email: e.target.value })}
                                placeholder="ap@client.com"
                                data-testid="bill-field-bemail"
                            />
                        </Field>
                        <Field label="Billing Phone">
                            <Input
                                value={form.billing_phone}
                                onChange={(e) => set({ billing_phone: e.target.value })}
                                placeholder="+91 98XXXXXXXX"
                                data-testid="bill-field-bphone"
                            />
                        </Field>
                    </Section>

                    {/* ── Tax classification ──────────────────────────── */}
                    <Section icon={<Receipt size={14} weight="duotone" />} title="Tax Classification">
                        <Field label="Place of Supply" hint="Drives Intra/Inter-state GST split on Tax Invoice">
                            <Input
                                value={form.place_of_supply}
                                onChange={(e) => set({ place_of_supply: e.target.value })}
                                placeholder="State name OR 'Outside India' for export"
                                data-testid="bill-field-pos"
                            />
                        </Field>
                        <Field label="GST Type" hint="Auto-derived if left blank: state-vs-supplier or country">
                            <select
                                value={form.gst_type}
                                onChange={(e) => set({ gst_type: e.target.value })}
                                className="w-full h-9 px-3 rounded border border-slate-200 bg-white text-sm focus:outline-none focus:border-indigo-500"
                                data-testid="bill-field-gst-type"
                            >
                                <option value="">— Auto-derive —</option>
                                {GST_TYPES.map((t) => <option key={t} value={t}>{t}</option>)}
                            </select>
                        </Field>
                        <Field label="Segment" hint="Internal classification">
                            <Input
                                value={form.segment}
                                onChange={(e) => set({ segment: e.target.value })}
                                placeholder="Bank / NBFC / AMC / SMB / Govt"
                                data-testid="bill-field-segment"
                            />
                        </Field>
                    </Section>

                    <DialogFooter className="border-t border-slate-100 -mx-6 px-6 pt-4 mt-2">
                        <FormDialogCancel disabled={busy} testId="bill-cancel-btn">
                            Cancel
                        </FormDialogCancel>
                        <Button type="submit" disabled={busy} data-testid="bill-save-btn">
                            {busy ? "Saving…" : isEdit ? "Save changes" : "Create client"}
                        </Button>
                    </DialogFooter>
                </form>
        </FormDialog>
    );
}

function Section({ icon, title, children }) {
    return (
        <div className="space-y-3">
            <div className="reedos-eyebrow flex items-center gap-1.5 text-[10px]">
                {icon}<span>{title}</span>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">{children}</div>
        </div>
    );
}

function Field({ label, hint, required, wide, children }) {
    return (
        <div className={wide ? "sm:col-span-2" : ""}>
            <Label className="text-[11px] reedos-eyebrow text-slate-600">
                {label}
                {required && <span className="text-rose-500 ml-0.5">*</span>}
            </Label>
            <div className="mt-1">{children}</div>
            {hint && <div className="text-[10px] text-slate-500 mt-1 leading-snug">{hint}</div>}
        </div>
    );
}
